import { computed, inject, Injectable, linkedSignal } from '@angular/core'
import { AuthService } from '@core/auth'
import { Month } from '@core/data'

/**
 * AppStore - Centralized application state management
 *
 * Responsibility: Manages global application state using signals.
 *
 * Rules:
 * - All state is reactive using signals and computed signals
 * - Uses linkedSignal for state that can be overridden
 * - Single source of truth for app-wide data
 * - Automatic computation with manual override capability
 *
 * Business Logic:
 * - Month cutoff: First 4 days of a month still display previous month
 * - Year computation: Adjusted based on cutoff days
 * - Months: Dynamic labels include current year
 *
 * Usage:
 * - Inject AppStore in components/services
 * - Access state: store.currentUser(), store.months(), store.selectedMonth()
 * - Override month: store.selectedMonth.set(6)
 * - Components auto-update when state changes
 */
@Injectable({
  providedIn: 'root'
})
export class AppStore {
  #authService = inject(AuthService)

  /**
   * Number of days at the start of a month that still count as the previous month.
   * Example: If set to 4, January 1-4 will display as "December" mode.
   */
  readonly #MONTH_CUTOFF_DAYS = 4

  /**
   * Current year (adjusted by cutoff days logic).
   * If we're within the cutoff period at the start of January, returns previous year.
   */
  readonly currentYear = computed(() => {
    const offset = this.#MONTH_CUTOFF_DAYS

    const date = new Date()
    date.setDate(date.getDate() - offset)

    return date.getFullYear()
  })

  /**
   * Available months for selection throughout the app.
   * Month values are formatted as "monthName_year" (e.g., "enero_2026").
   * Labels include the year for clarity.
   * Computed dynamically based on currentYear signal.
   */
  readonly months = computed<Month[]>(() => {
    const currentYear = this.currentYear()

    return [
      { value: `enero_${currentYear}`, label: `Enero ${currentYear}` },
      { value: `febrero_${currentYear}`, label: `Febrero ${currentYear}` },
      { value: `marzo_${currentYear}`, label: `Marzo ${currentYear}` },
      { value: `abril_${currentYear}`, label: `Abril ${currentYear}` },
      { value: `mayo_${currentYear}`, label: `Mayo ${currentYear}` },
      { value: `junio_${currentYear}`, label: `Junio ${currentYear}` },
      { value: `julio_${currentYear}`, label: `Julio ${currentYear}` },
      { value: `agosto_${currentYear}`, label: `Agosto ${currentYear}` },
      { value: `septiembre_${currentYear}`, label: `Septiembre ${currentYear}` },
      { value: `octubre_${currentYear}`, label: `Octubre ${currentYear}` },
      { value: `noviembre_${currentYear}`, label: `Noviembre ${currentYear}` },
      { value: `diciembre_${currentYear}`, label: `Diciembre ${currentYear}` }
    ]
  })

  /**
   * Current selected month.
   * Uses linkedSignal for automatic computation with manual override capability.
   *
   * Default behavior:
   * - Computed based on current date and MONTH_CUTOFF_DAYS
   * - If current day <= cutoff, displays previous month
   * - Example: On January 3rd (cutoff=4), displays December of the previous year,
   *   since `months` is already built from the cutoff-adjusted `currentYear`
   *
   * Manual override:
   * - Can be set manually: store.selectedMonth.set(decemberMonth)
   * - Can be updated: store.selectedMonth.update(m => nextMonth)
   */
  readonly selectedMonth = linkedSignal<Month>(() => {
    const date = new Date()
    const day = date.getDate()

    const startOffset = day <= this.#MONTH_CUTOFF_DAYS ? 1 : 0
    // Wraps around so January within the cutoff lands on December instead of falling back to January
    const monthIndex = (date.getMonth() - startOffset + 12) % 12

    return this.months()[monthIndex]
  })

  /**
   * Current calendar month, with no cutoff-days adjustment.
   * Unlike `selectedMonth`, this always reflects the real current month (e.g. August 1st is
   * already "Agosto"). Used by features that must not be affected by the fiscal cutoff.
   */
  readonly currentMonth = computed<Month>(() => {
    const monthIndex = new Date().getMonth()
    return this.months()[monthIndex]
  })

  /**
   * Current authenticated user (computed from AuthService).
   * Returns null when no user is authenticated.
   * Components using this signal will automatically update when auth state changes.
   */
  readonly currentUser = computed(() => this.#authService.currentUser())

  /**
   * Whether user is authenticated (computed from AuthService).
   * Returns true if a user is logged in, false otherwise.
   * Components using this signal will automatically update when auth state changes.
   */
  readonly isAuthenticated = computed(() => this.#authService.isAuthenticated())

  async updateUser(data: Record<string, unknown>) {
    await this.#authService.updateCurrentUser(data)
    await this.#authService.loadCurrentUser()
  }

  async reloadUser() {
    await this.#authService.loadCurrentUser()
  }
}
